import React from 'react';
import styled from '@emotion/styled';
import {
  injectIntl,
  WrappedComponentProps,
  useIntl,
  FormattedMessage,
  IntlShape,
} from 'react-intl';
import algoliasearch from 'algoliasearch/lite';
import { MultipleQueriesQuery } from '@algolia/client-search';
import { getQueryParam } from '../scripts/getQueryParam';
import {
  InstantSearch,
  Configure,
  connectSearchBox,
  connectInfiniteHits,
  connectStateResults,
} from 'react-instantsearch-dom';
import {
  Grid,
  GridItem,
  Spacer,
  Container,
  Main,
  Burrito,
} from 'ech-components/src/helpers';
import { H1 } from 'ech-components/src/primitives';
import {
  Header,
  Footer,
  Helmet,
  ThemeLayout,
  Hits,
  NoResults,
} from '@components';
import { ScrollToTop, SearchToolbar } from 'ech-components/src/components';
import { colors } from 'ech-components/src/core/styles/theme/default';
import { createLanguageObject } from '@scripts/utils/domain';

const PAGE_ID = 'search';

const StyledTitle = styled(H1)`
  padding-right: 2rem;
`;

const StyledBurrito = styled(Burrito)`
  .offsetBackground {
    background-color: ${colors.primarySunYellow300};
  }
`;

const CustomHits = connectInfiniteHits(Hits);
const CustomNoResults = connectStateResults(NoResults);
const CustomSearchBox = connectSearchBox(
  ({ currentRefinement }: { currentRefinement: string }) => {
    const intl = useIntl();

    return (
      <SearchToolbar
        defaultInputValue={currentRefinement}
        searchButtonText={intl.formatMessage({ id: 'search.submit' })}
        filtersHidden
      />
    );
  },
);

const SearchPage = ({ intl, pageContext }: SearchProps) => {
  const searchInput = getQueryParam('searchInput') as string;

  const algoliaSearchClient = algoliasearch(
    process.env.GATSBY_ALGOLIA_APP_ID || '',
    process.env.GATSBY_ALGOLIA_SEARCH_API_KEY || '',
  );

  const searchClient = {
    search(requests: MultipleQueriesQuery[]) {
      if (!searchInput) {
        return;
      }

      return algoliaSearchClient.search(requests);
    },
  };

  return (
    <ThemeLayout
      customTheme={{
        campaign: {
          key: pageContext.campaignKey,
        },
      }}
    >
      <Helmet
        locale={intl.locale}
        langObj={createLanguageObject({ intl: intl, root: PAGE_ID, slug: '' })}
        description={intl.formatMessage({ id: `page.seo.search.description` })}
        title={intl.formatMessage({ id: `page.seo.search.title` })}
        matomo={pageContext.matomo}
      />
      <Header
        langObj={createLanguageObject({ intl: intl, root: PAGE_ID, slug: '' })}
      />
      <Main>
        <Container>
          <Spacer space="giga" />
          <StyledTitle>
            {searchInput ? (
              <FormattedMessage
                id="search.title"
                values={{ query: searchInput }}
              />
            ) : (
              <FormattedMessage id="search.empty.title" />
            )}
          </StyledTitle>
          <Spacer space="giga" />
          <InstantSearch
            indexName={
              `${process.env.GATSBY_ALGOLIA_INDEX_NAME_PREFIX}_${intl.locale}` ||
              ''
            }
            searchClient={searchClient}
          >
            <Grid columns={{ zero: 12, small: 12 }}>
              <GridItem size={{ zero: 'full', small: 12 }}>
                <Configure hitsPerPage={192} distinct />
                <StyledBurrito hasBackground spacingCurve="kilo">
                  <CustomSearchBox
                    defaultRefinement={searchInput || undefined}
                  />
                </StyledBurrito>
              </GridItem>
            </Grid>
            <CustomHits />
            <CustomNoResults />
          </InstantSearch>
        </Container>
        <Spacer space="peta" />
        <ScrollToTop
          backToTopText={intl.formatMessage({
            id: 'page.backToTop',
          })}
        />
        <Spacer space="peta" />
      </Main>
      <Footer
        langObj={createLanguageObject({ intl: intl, root: PAGE_ID, slug: '' })}
      />
    </ThemeLayout>
  );
};

export type SearchProps = {
  data: any;
  intl: IntlShape;
  pageContext: any;
} & WrappedComponentProps;

export default injectIntl(SearchPage);
